import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../App';
import { useAuth } from '../AuthContext';
import { t } from '../i18n';
import LanguageToggle from '../components/game/LanguageToggle';
import Sidebar from '../components/game/Sidebar';
import UpgradePrompt from '../components/game/UpgradePrompt';
import OnboardingTutorial from '../components/game/OnboardingTutorial';
import WeakSubjectBanner from '../components/game/WeakSubjectBanner';
import PetWidget from '../components/game/PetWidget';
import EmailVerifyBanner from '../components/game/EmailVerifyBanner';
import PetAlerts from '../components/game/PetAlerts';
import { Calculator, BookOpen, Globe, FlaskConical, Clock, MapPin, Monitor, Leaf, Lock, Flame, Coins, Target, Gift, Zap, Play, Users } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SUBJECTS = [
  { id: 'matematik', icon: Calculator, color: '#E85D3A', titleKey: 'subjectMath', subjectType: 'math' },
  { id: 'bahasa-melayu', icon: BookOpen, color: '#2196F3', titleKey: 'subjectBM', subjectType: 'mcq' },
  { id: 'english', icon: Globe, color: '#E85D3A', titleKey: 'subjectEN', subjectType: 'mcq' },
  { id: 'sains', icon: FlaskConical, color: '#009688', titleKey: 'subjectScience', subjectType: 'mcq' },
  { id: 'sejarah', icon: Clock, color: '#3b82f6', titleKey: 'subjectHistory', subjectType: 'mcq' },
  { id: 'geografi', icon: MapPin, color: '#4CAF50', titleKey: 'subjectGeography', subjectType: 'mcq' },
  { id: 'informatik', icon: Monitor, color: '#8BC34A', titleKey: 'subjectIT', subjectType: 'mcq' },
  { id: 'biologi', icon: Leaf, color: '#7CB342', titleKey: 'subjectBiology', subjectType: 'mcq' },
];

// Mascot SVG Component
function Mascot({ size = 60, mood = 'happy' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
      {/* Body (rope character) */}
      <circle cx="40" cy="38" r="28" fill="#FFE0B2" />
      <circle cx="40" cy="38" r="28" stroke="#F59E0B" strokeWidth="3" />
      {/* Eyes */}
      <circle cx="30" cy="33" r="4" fill="#1e293b" />
      <circle cx="50" cy="33" r="4" fill="#1e293b" />
      <circle cx="31.5" cy="31.5" r="1.5" fill="white" />
      <circle cx="51.5" cy="31.5" r="1.5" fill="white" />
      {/* Mouth */}
      {mood === 'happy' && <path d="M30 45 Q40 55 50 45" stroke="#1e293b" strokeWidth="2.5" strokeLinecap="round" fill="none" />}
      {mood === 'excited' && <ellipse cx="40" cy="47" rx="8" ry="5" fill="#1e293b" />}
      {/* Cheeks */}
      <circle cx="24" cy="42" r="4" fill="#FFAB91" opacity="0.5" />
      <circle cx="56" cy="42" r="4" fill="#FFAB91" opacity="0.5" />
      {/* Crown/hat for fun */}
      <path d="M25 18 L30 10 L35 16 L40 8 L45 16 L50 10 L55 18" fill="#F59E0B" stroke="#E88B0E" strokeWidth="1.5" />
      {/* Rope arms */}
      <path d="M12 42 Q8 38 12 34" stroke="#8B6914" strokeWidth="4" strokeLinecap="round" />
      <path d="M68 42 Q72 38 68 34" stroke="#8B6914" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user, token, canAccessSubject, canPlay } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const [gData, setGData] = useState(null);
  const isEN = language === 'en';

  useEffect(() => {
    if (token) {
      fetch(`${BACKEND_URL}/api/gamification/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setGData(data); })
        .catch(() => {});
    }
  }, [token]);

  const handleCardClick = (subject, index) => {
    if (user && !canAccessSubject(index)) {
      setUpgradeFeature(t(subject.titleKey, language));
      setShowUpgrade(true);
      return;
    }
    if (user && !canPlay()) {
      setUpgradeFeature(t('unlimitedGames', language));
      setShowUpgrade(true);
      return;
    }
    navigate('/setup', { state: { subjectId: subject.id, subjectType: subject.subjectType } });
  };

  // Quick Play — random unlocked subject, solo vs AI, 60s
  const quickPlay = () => {
    if (user && !canPlay()) {
      setUpgradeFeature(t('unlimitedGames', language));
      setShowUpgrade(true);
      return;
    }
    const subjectLimit = user ? ({ free: 2, basic: 8, pro: 8, school: 8 }[user.plan] || 2) : 8;
    const available = SUBJECTS.slice(0, subjectLimit);
    const pick = available[Math.floor(Math.random() * available.length)];
    navigate('/solo-game', {
      state: {
        team1Name: user?.name || 'Player', team2Name: 'AI Bot', playerName: user?.name || 'Player',
        difficulty: 'easy', operations: ['+', '-'], duration: 60,
        subjectId: pick.id, subjectType: pick.subjectType, language,
      }
    });
  };

  const plan = user?.plan || 'free';
  const gamesUsed = user?.games_this_month || 0;
  const gamesLimit = user ? ({ free: 25, basic: 999999, pro: 999999, school: 999999 }[plan] || 25) : 999999;

  const streakDays = gData?.current_streak || 0;
  const level = gData?.level || 1;
  const rank = gData?.rank || {};
  const coins = gData?.coins || 0;
  const xp = gData?.xp || 0;
  const xpNext = gData?.xp_next_level || 300;
  const xpCurrent = gData?.xp_current_level || 100;
  const xpProgress = xpNext > xpCurrent ? Math.min(((xp - xpCurrent) / (xpNext - xpCurrent)) * 100, 100) : 0;
  const quests = gData?.quests || [];
  const completedQuests = quests.filter(q => q.completed).length;

  return (
    <div className="dashboard-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} gData={gData} />
      <UpgradePrompt show={showUpgrade} onClose={() => setShowUpgrade(false)}
        feature={upgradeFeature} requiredPlan="Basic" />
      <OnboardingTutorial />

      <header className="dashboard-header">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)} data-testid="sidebar-toggle" aria-label="Open menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <h1 className="dashboard-header-title">{isEN ? 'Play' : 'Main'}</h1>
        {user && gData && (
          <div className="header-coins" data-testid="header-coins" onClick={() => navigate('/shop')}>
            <Coins size={16} /> <span>{coins}</span>
          </div>
        )}
        <LanguageToggle className="dashboard-lang-toggle" />
      </header>

      <div className="dashboard-content">
        {/* Gamification Panel */}
        {user && gData && (
          <motion.div className="gami-panel" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} data-testid="gamification-panel">
            <div className="gami-top-row">
              {/* Streak with glow */}
              <div className={`gami-streak ${streakDays > 0 ? 'gami-streak-active' : ''}`} data-testid="streak-display">
                <div className="gami-streak-fire">
                  <Flame size={22} />
                  <span className="gami-streak-num">{streakDays}</span>
                </div>
                <span className="gami-streak-label">{isEN ? 'Day Streak' : 'Hari Streak'}</span>
              </div>

              {/* Level + XP Bar with animated gradient */}
              <div className="gami-level" data-testid="level-display" onClick={() => navigate('/progress')}>
                <div className="gami-level-badge" style={{ background: rank.color || '#9ca3af' }}>
                  <span>{rank.icon || '\ud83c\udf31'}</span>
                  <span className="gami-level-num">Lv.{level}</span>
                </div>
                <div className="gami-xp-bar-wrap">
                  <motion.div className="gami-xp-bar" initial={{ width: 0 }} animate={{ width: `${xpProgress}%` }} transition={{ duration: 1, ease: 'easeOut' }} />
                </div>
                <span className="gami-xp-text">{xp}/{xpNext} XP</span>
              </div>

              <div className="gami-rank" data-testid="rank-display">
                <span className="gami-rank-name" style={{ color: rank.color }}>{isEN ? rank.name_en : rank.name_bm}</span>
              </div>
            </div>

            {/* Daily Quests */}
            <div className="gami-quests" data-testid="daily-quests">
              <div className="gami-quests-header">
                <Target size={16} />
                <span>{isEN ? 'Daily Quests' : 'Misi Harian'}</span>
                <span className="gami-quests-count">{completedQuests}/{quests.length}</span>
              </div>
              <div className="gami-quests-list">
                {quests.map((q, i) => (
                  <motion.div key={i} className={`gami-quest-item ${q.completed ? 'gami-quest-done' : ''}`}
                    data-testid={`quest-${i}`}
                    initial={q.completed ? { scale: 1 } : {}}
                    animate={q.completed ? { scale: [1, 1.05, 1] } : {}}
                    transition={{ duration: 0.3 }}>
                    <div className="gami-quest-check">{q.completed ? '\u2713' : (i + 1)}</div>
                    <div className="gami-quest-info">
                      <span className="gami-quest-name">{isEN ? q.name_en : q.name_bm}</span>
                      <div className="gami-quest-progress-bar">
                        <motion.div className="gami-quest-progress-fill"
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min((q.progress / q.target) * 100, 100)}%` }}
                          transition={{ duration: 0.8, delay: i * 0.15 }} />
                      </div>
                    </div>
                    <div className="gami-quest-reward"><Coins size={12} /> {q.reward_coins}</div>
                  </motion.div>
                ))}
              </div>
            </div>

            {streakDays > 0 && streakDays < 7 && (
              <div className="gami-streak-hint">
                <Gift size={14} />
                <span>{isEN ? `${7 - streakDays} more days for 100 bonus coins!` : `${7 - streakDays} hari lagi untuk 100 syiling bonus!`}</span>
              </div>
            )}
          </motion.div>
        )}

        {/* Pet Alerts */}
        {user && <PetAlerts />}

        {/* Email Verification Banner */}
        {user && <EmailVerifyBanner />}

        {/* Pet Widget */}
        {user && <PetWidget />}

        {/* Quick Play + Mascot */}
        <motion.div className="hp-quick-play" data-testid="quick-play-section"
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <div className="hp-mascot-wrap">
            <motion.div animate={{ y: [0, -6, 0] }} transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}>
              <Mascot size={56} mood={streakDays > 0 ? 'excited' : 'happy'} />
            </motion.div>
          </div>
          <div className="hp-quick-info">
            <h2 className="hp-quick-title">{isEN ? 'Ready to play?' : 'Sedia untuk main?'}</h2>
            <p className="hp-quick-desc">{isEN ? 'Jump into a game instantly!' : 'Terus main sekarang!'}</p>
          </div>
          <motion.button className="hp-quick-btn" onClick={quickPlay} data-testid="quick-play-btn"
            whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
            <Play size={20} fill="white" /> {isEN ? 'QUICK PLAY' : 'MAIN CEPAT'}
          </motion.button>
        </motion.div>

        {/* Race Mode (Classroom) — visible for School plan */}
        {user && plan === 'school' && (
          <motion.button className="hp-race-btn" onClick={() => navigate('/classroom')}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} data-testid="race-mode-btn">
            <Users size={20} />
            <span>{isEN ? 'RACE MODE — Live Classroom' : 'MOD LUMBA — Bilik Darjah Live'}</span>
          </motion.button>
        )}

        <WeakSubjectBanner />

        {user && plan === 'free' && (
          <div className="games-counter">
            <span>{t('gamesUsed', language)}: <strong>{gamesUsed}/{gamesLimit}</strong></span>
            {gamesUsed >= gamesLimit && <span className="games-limit-hit">{t('limitReached', language)}</span>}
          </div>
        )}

        {/* Compact Subject Grid */}
        <h3 className="hp-subjects-title">{isEN ? 'Choose Subject' : 'Pilih Subjek'}</h3>
        <div className="hp-subject-grid" data-testid="subject-grid">
          {SUBJECTS.map((subject, index) => {
            const Icon = subject.icon;
            const isLocked = user && !canAccessSubject(index);
            return (
              <motion.button
                key={subject.id}
                className={`hp-subject-btn ${isLocked ? 'hp-subject-locked' : ''}`}
                onClick={() => handleCardClick(subject, index)}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                whileHover={!isLocked ? { scale: 1.06, y: -3 } : {}}
                whileTap={!isLocked ? { scale: 0.95 } : {}}
                data-testid={`subject-card-${subject.id}`}
              >
                <div className="hp-subject-icon" style={{ background: isLocked ? '#d1d5db' : subject.color }}>
                  <Icon size={22} color="white" strokeWidth={2.2} />
                </div>
                <span className="hp-subject-name">{t(subject.titleKey, language)}</span>
                {isLocked && <Lock size={12} className="hp-subject-lock" />}
              </motion.button>
            );
          })}
        </div>
        <div data-testid="powered-by" style={{ textAlign: 'center', padding: '24px 0 16px', fontSize: 12, color: '#94a3b8', letterSpacing: '0.05em' }}>POWERED BY UNDERADS</div>
      </div>
    </div>
  );
}
